import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import {
  type EpocusReview,
  RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-review";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { useUser } from "$global-providers/user-provider/provider";
import { useCallback, useMemo } from "react";
import type { RxCollection } from "rxdb";
import { useRxData } from "rxdb-hooks";
import {
  getTurnaroundMinutes,
  getTurnaroundStatus,
  median,
  resolveTargetMinutes,
} from "./review-turnaround";
import {
  DEFAULT_REVIEWER_WINDOW,
  isWithinReviewerWindow,
  type ReviewerWindowKey,
  reviewerWindowStart,
} from "./reviewer-stats-window";
import { useActiveProgram } from "./use-active-program";

const DAY_MS = 24 * 60 * 60 * 1000;

export type ReviewerTurnaroundStats = {
  reviewedToday: number;
  sourcesToday: number;
  medianTurnaroundMinutes: number | null;
  withinTargetPct: number | null;
  targetMinutes: number;
};

export const useReviewerTurnaroundStats = (
  windowKey: ReviewerWindowKey = DEFAULT_REVIEWER_WINDOW,
): ReviewerTurnaroundStats => {
  const { id: userId } = useUser();
  const { getDisplayTime } = useServerTime();
  const program = useActiveProgram();

  const reviewsQuery = useCallback(
    (collection: RxCollection<EpocusReview>) =>
      collection.find({ selector: { reviewerId: { $eq: userId } } }),
    [userId],
  );
  const { result: reviews } = useRxData<EpocusReview>(
    RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
    reviewsQuery,
  );

  const examIds = useMemo(
    () => reviews.map((review) => review.examId),
    [reviews],
  );

  const examsQuery = useCallback(
    (collection: RxCollection<EpocusExam>) =>
      examIds.length > 0
        ? collection.find({ selector: { id: { $in: examIds } } })
        : collection.find({ selector: { id: { $eq: "__none__" } } }),
    [examIds],
  );
  const { result: exams } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    examsQuery,
  );

  const encounterIds = useMemo(
    () => exams.map((exam) => exam.encounterId),
    [exams],
  );

  const encountersQuery = useCallback(
    (collection: RxCollection<EpocusEncounter>) =>
      encounterIds.length > 0
        ? collection.find({ selector: { id: { $in: encounterIds } } })
        : collection.find({ selector: { id: { $eq: "__none__" } } }),
    [encounterIds],
  );
  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    encountersQuery,
  );

  const targetMinutes = resolveTargetMinutes(program?.turnaroundTargetMinutes);

  return useMemo(() => {
    const now = getDisplayTime().getTime();
    const windowStart = reviewerWindowStart(windowKey, now);

    const encounterById = new Map(encounters.map((e) => [e.id, e]));
    const encounterForExam = new Map(
      exams.map((exam) => [exam.id, encounterById.get(exam.encounterId)]),
    );

    const signed = reviews.filter((review) => review.signedAt !== null);

    const signedToday = signed.filter((review) => {
      const signedAt = new Date(review.signedAt ?? "").getTime();
      return Number.isFinite(signedAt) && now - signedAt < DAY_MS;
    });

    const performersToday = new Set(
      signedToday
        .map((review) => encounterForExam.get(review.examId)?.performerId)
        .filter((performerId): performerId is string => Boolean(performerId)),
    );

    const inWindow = signed.filter((review) =>
      isWithinReviewerWindow(review.signedAt, windowStart),
    );

    const measured = inWindow
      .map((review) => {
        const encounter = encounterForExam.get(review.examId);
        return encounter ? getTurnaroundMinutes(review, encounter) : null;
      })
      .filter((minutes): minutes is number => minutes !== null);

    const medianMinutes = median(measured);
    const withinTarget = measured.filter(
      (minutes) =>
        getTurnaroundStatus(minutes, targetMinutes) === "within_target",
    ).length;

    return {
      reviewedToday: signedToday.length,
      sourcesToday: performersToday.size,
      medianTurnaroundMinutes:
        medianMinutes === null ? null : Math.round(medianMinutes),
      withinTargetPct:
        measured.length > 0
          ? Math.round((withinTarget / measured.length) * 100)
          : null,
      targetMinutes,
    };
  }, [reviews, exams, encounters, targetMinutes, windowKey, getDisplayTime]);
};
